import React, { useEffect, useState } from "react";
import './BillableEvent.css';
import config from "../../config/config";
import { userSession } from "../../observers/userObserver";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const COLORS = ["#006565", "#bcffff"];

const BillableEvent = () => {
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState("all");
  const [billingUsage, setBillingUsage] = useState(null);
  const [horizontalData, setHorizontalData] = useState([]);
  const [pieData, setPieData] = useState([]);
  const [kpiTableData, setKpiTableData] = useState([]);
  const [chartKey, setChartKey] = useState(0);

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const token = userSession.getToken();

        const response = await fetch(`${config["backend-url"]}/customers`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          }
        });

        const data = await response.json();
        setCustomers(data);
      } catch (err) {
        console.error("Failed to fetch customers:", err);
      }
    };

    fetchCustomers();
  }, []);


  const fetchBillingUsage = async (customerName) => {
    if (customerName === "all") {
      setBillingUsage(null);
      setHorizontalData([]);
      setPieData([]);
      setKpiTableData([]);
      return;
    }
  
    try {
      const token = userSession.getToken();
      const response = await fetch(
        `${config["backend-url"]}/get_billing_usage_by_customer`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ customer_name: customerName }),
        }
      );
  
      const data = await response.json();
      setBillingUsage(data);
  
      if (data.month) {
        setBillingMonth(data.month);
      }
  
      const usage = data.billing_usage || {};
      updateUsageCharts(usage);
    } catch (err) {
      console.error("Error fetching billing usage:", err);
    }
  
    setChartKey(prev => prev + 1); // triggers re-render
  };
  

  useEffect(() => {
  {
      fetchBillingUsage();
    }
  }, [selectedCustomer]);


  const updateUsageCharts = (usage) => {
    // KPI Table Data calculation remains same
    const kpiData = [
      {
        metric: "Total Allowed",
        "Create Request": usage["Create Request"]?.allowed || 0,
        Finalized: usage["Finalize Request"]?.allowed || 0,
        "COD Communication": usage["COD Communication"]?.allowed || 0,
        "Open Request": usage["open Request"]?.allowed || 0,
      },
      {
        metric: "Used",
        "Create Request": usage["Create Request"]?.used || 0,
        Finalized: usage["Finalize Request"]?.used || 0,
        "COD Communication": usage["COD Communication"]?.used || 0,
        "Open Request": usage["open Request"]?.used || 0,
      },
      {
        metric: "Remaining",
        "Create Request":
          (usage["Create Request"]?.allowed || 0) -
          (usage["Create Request"]?.used || 0),
        Finalized:
          (usage["Finalize Request"]?.allowed || 0) -
          (usage["Finalize Request"]?.used || 0),
        "COD Communication":
          (usage["COD Communication"]?.allowed || 0) -
          (usage["COD Communication"]?.used || 0),
        "Open Request":
          (usage["open Request"]?.allowed || 0) -
          (usage["open Request"]?.used || 0),
      },
    ];
    setKpiTableData(kpiData);

    // Bar Chart - convert 'Used' and 'Remaining' to percentage of allowed
    const transformed = Object.entries(usage).map(
      ([eventName, { allowed = 0, used = 0 }]) => {
        const percentUsed = allowed > 0 ? (used / allowed) * 100 : 0;
        const percentRemaining = allowed > 0 ? 100 - percentUsed : 0;
        return {
          name: eventName,
          Used: percentUsed,
          Remaining: percentRemaining,
          usedRaw: used,
          allowedRaw: allowed,
        };
      }
    ).filter(item => item.name !== "open Request"); // keep consistent with original

    setHorizontalData(transformed);

    // Pie Chart
    const openRequest = usage["open Request"];
    if (openRequest) {
      const pie = [
        { name: "Used", value: openRequest.used },
        { name: "Remaining", value: Math.max(openRequest.allowed - openRequest.used, 0) },
      ];
      setPieData(pie);
    } else {
      setPieData([]);
    }
  };
const [billingMonth, setBillingMonth] = useState('');
  const handleCustomerChange = async (e) => {
    const name = e.target.value;
    setSelectedCustomer(name);

    if (name === "all") {
      setBillingUsage(null);
      setHorizontalData([]);
      setPieData([]);
      setKpiTableData([]);
      return;
    }

    try {
      const token = userSession.getToken();
      const response = await fetch(
        `${config["backend-url"]}/get_billing_usage_by_customer`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ customer_name: name }),
        }
      );

      const data = await response.json();
      setBillingUsage(data);
 // ✅ Set the billing month from response
    if (data.month) {
      setBillingMonth(data.month);
    }
      const usage = data.billing_usage || {};
      updateUsageCharts(usage);
    } catch (err) {
      console.error("Error fetching billing usage:", err);
    }
    setChartKey(prev => prev + 1); // triggers re-render
  };

  
  // Custom Tooltip to show absolute values and % for bars
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const usedPercent = payload.find(p => p.dataKey === "Used")?.value || 0;
      const remainingPercent = payload.find(p => p.dataKey === "Remaining")?.value || 0;

      const usedRaw = payload.find(p => p.dataKey === "Used")?.payload.usedRaw || 0;
      const allowedRaw = payload.find(p => p.dataKey === "Used")?.payload.allowedRaw || 0;
      const remainingRaw = allowedRaw - usedRaw;

      return (
        <div className="custom-tooltip" style={{ background: '#fff', border: '1px solid #ccc', padding: '10px' }}>
          <p><strong>{label}</strong></p>
          <p>Used: {usedRaw} ({usedPercent.toFixed(2)}%)</p>
          <p>Remaining: {remainingRaw} ({remainingPercent.toFixed(2)}%)</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="billable-event-container">
      <div className="billable-event-header">
        <h4 className="welcomeText">Billable Event</h4>
        <div className="customer-select-container">
          <select
            id="customerSelect"
            className="customer-select"
            value={selectedCustomer}
            onChange={handleCustomerChange}
          >
            <option value="all">All Customers</option>
            {customers.map((name, index) => (
              <option key={index} value={name}>{name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="billable-event-columns">
        {/* KPI Table */}
        <div className="kpi-section">
          <h4 className="section-title">Company Data - {billingMonth || ''}</h4>
          {/* <table className="kpi-table">
            <thead>
              <tr>
                <th>Billable Event</th>
                <th>Create Request</th>
                <th>Finalized</th>
                <th>COD Communication</th>
                <th>Open Request</th>
              </tr>
            </thead>
            <tbody>
              {kpiTableData.map((item, index) => (
                <tr key={index}>
                  <td>{item.metric}</td>
                  <td><strong>{item["Create Request"]}</strong></td>
                  <td><strong>{item["Finalized"]}</strong></td>
                  <td><strong>{item["COD Communication"]}</strong></td>
                  <td><strong>{item["open Request"]}</strong></td>
                </tr>
              ))}
            </tbody>
          </table> */}
          <table className="kpi-table">
  <thead>
    <tr>
      <th>Event Type</th>
      {kpiTableData.map((item, index) => (
        <th key={index}>{item.metric}</th>
      ))}
    </tr>
  </thead>
  <tbody>
    {["Create Request", "Finalized", "COD Communication", "Open Request"].map((event, index) => (
      <tr key={index}>
        <td>{event}</td>
        {kpiTableData.map((item, i) => (
          <td key={i}><strong>{item[event]}</strong></td>
        ))}
      </tr>
    ))}
  </tbody>
</table>

        </div>

        {/* Horizontal Stacked Bar Graph */}
        <div className="horizontal-graph-section" key={`bar-${chartKey}`}>
          <h4 className="section-title">Billable Event - {billingMonth || ' '}</h4>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart
              layout="vertical"
              data={horizontalData}
              margin={{ top: 10, right: 30, left: 10, bottom: 0 }}
            >
              <XAxis 
                type="number" 
                domain={[0, 100]} 
                ticks={[0, 20, 40, 60, 80, 100]} 
                unit="%" 
                tickFormatter={(tick) => `${tick}%`} 
              />
              <YAxis dataKey="name" type="category" />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="Remaining" stackId="a" fill="#006565" />
              <Bar dataKey="Used" stackId="a" fill="#dc3545" />
            </BarChart>
          </ResponsiveContainer>

          <div className="graph-legend">
            <div className="legend-item">
              <span className="legend-color" style={{ backgroundColor: "#006565" }}></span>Remaining
            </div>
            <div className="legend-item">
              <span className="legend-color" style={{ backgroundColor: "#dc3545" }}></span>Used
            </div>
          </div>
        </div>

        {/* Pie Chart */}
        <div className="pie-chart-section" key={`pie-${chartKey}`}>
          <h4 className="section-title">Open Request</h4>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                dataKey="value"
                label
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="graph-legend">
            {pieData.map((entry, index) => (
              <div className="legend-item" key={`legend-${index}`}>
                <span className="legend-color" style={{ backgroundColor: COLORS[index % COLORS.length] }}></span>
                {entry.name}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillableEvent;
